import {call, put, select, takeEvery, takeLatest} from 'redux-saga/effects'
import get from 'lodash/get'
import head from 'lodash/head'

import {
  APPLY_PROMO_CODE,
  applyPromoCodeFailure,
  applyPromoCodePending,
  applyPromoCodeSuccess,
  displayOverride,
  resetPromo,
} from '../actions/promo'
import {currentlyAppliedPromoCode, isOverrideVisible} from '../store/promo'
import {currentStep, orderDiscountTotal, orderPromoCode} from '../store/checkout'
import {NAVIGATE_TO_CHECKOUT_STEP, updateCheckoutVersions} from '../actions/checkout'
import * as api from '../api/api'
import {isBadResponse} from '../api/statusCodes'
import {CheckoutStepSlugs} from '../store/enum'

export function* applyPromoCodeWorker({payload}) {
  try {
    const appliedPromoCode = yield select(currentlyAppliedPromoCode)
    const overrideIsDisplayed = yield select(isOverrideVisible)
    const orderPromo = yield select(orderPromoCode)
    const orderPromoDiscountTotal = yield select(orderDiscountTotal)

    // if a promo has already been successfully applied and the current discount total > 0
    // prompt the user to confirm overriding the currently applied promo
    if (orderPromoDiscountTotal > 0 && (orderPromo || appliedPromoCode) && !overrideIsDisplayed) {
      yield put(displayOverride())
    } else {
      yield put(applyPromoCodePending())

      const response = yield call(api.applyPromoCode, payload)

      // check if we have a bad API response
      if (isBadResponse(response.status)) {
        throw new Error('An error occurred applying the promo code. Please try again.')
      }

      const responseData = get(response, 'json', {})
      // The versions returned from the API provide a "signature" that is unnecessary and needs to be removed
      const billingVersion = +head(get(responseData, 'billing_version', '').split(':'))
      const shippingVersion = +head(get(responseData, 'shipping_version', '').split(':'))

      // update the current versions saved
      yield put(
        updateCheckoutVersions({
          billingVersion,
          shippingVersion,
        }),
      )

      // check if we have a 200, but API returns error code (eg: input promo does not exist, expired, etc.)
      const {error} = responseData
      if (error) {
        throw new Error(error)
      }

      yield put(
        applyPromoCodeSuccess({
          appliedCode: payload,
          discountAmount: get(responseData, 'discount'),
        }),
      )
    }
  } catch (e) {
    yield put(applyPromoCodeFailure(e.message))
  }
}

export function* applyPromoCodeSaga() {
  yield takeLatest(APPLY_PROMO_CODE, applyPromoCodeWorker)
}

/**
 * Resets the promo Redux state when navigating away from the billing step.
 *
 * @returns {IterableIterator<*>}
 */
export function* resetPromoWorker() {
  const checkoutStep = yield select(currentStep)
  if (checkoutStep !== CheckoutStepSlugs.billing) {
    yield put(resetPromo())
  }
}

export function* resetPromoSaga() {
  yield takeEvery(NAVIGATE_TO_CHECKOUT_STEP, resetPromoWorker)
}
